import { useState } from "react";
import { cn } from "../../lib/utils";
import { getAvatarToneIndex, getInitials } from "../../logic/households";

const TONES = [
  "bg-surface-raised text-muted-foreground",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200",
  "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",
  "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",
  "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
] as const;

const SIZES = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-16 h-16 text-lg",
} as const;

interface AvatarProps {
  name: string | null | undefined;
  /** Seed for the deterministic color (defaults to name). */
  seed?: string | null;
  photoUrl?: string | null;
  size?: keyof typeof SIZES;
  /** "square" for households/workspaces, "circle" for people. */
  shape?: "square" | "circle";
  className?: string;
}

/**
 * Initials avatar with a stable pastel color, optionally showing a photo
 * (falls back to initials if the photo fails to load).
 */
export function Avatar({
  name,
  seed,
  photoUrl,
  size = "md",
  shape = "circle",
  className,
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const tone = TONES[getAvatarToneIndex(seed ?? name)];
  const shapeClass = shape === "square" ? "rounded-xl" : "rounded-full";

  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt=""
        onError={() => setFailed(true)}
        className={cn(SIZES[size], shapeClass, "object-cover shrink-0 bg-surface-raised", className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center justify-center font-bold shrink-0 select-none",
        SIZES[size],
        shapeClass,
        tone,
        className
      )}
    >
      {getInitials(name)}
    </span>
  );
}

export default Avatar;
